'use client';

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type {
  BrowserCookie,
  BrowserCookieType,
  Consent,
} from '@personly/libs-types';
import CookieHandler, { CookieAttributes } from '@personly/libs-cookie';

type CookiesContextType = {
  setCookie: (name: string, data: string) => CookieAttributes;
  getCookie: (name: string) => string | null | undefined;
  setConsent: (consent: Consent) => void;
  consent: Consent;
};

interface Props {
  children: ReactNode;
  cookies: BrowserCookie[];
  cookieTypes: BrowserCookieType[];
  hostname: string;
  consentName?: string;
}

const CookiesContext = createContext<CookiesContextType | null>(null);

const makeRequiredTypes = (cookieTypes: BrowserCookieType[]) =>
  cookieTypes
    .filter(({ flags }) => flags.isRequired)
    .map(({ handle }) => handle);

const CookiesProvider = ({
  children,
  cookies,
  cookieTypes,
  hostname,
  consentName = 'consent',
}: Props) => {
  const cookieHandler = useMemo(
    () => new CookieHandler(hostname, cookies),
    [hostname, cookies]
  );

  const getConsent = useCallback(() => {
    const types = makeRequiredTypes(cookieTypes);
    const data = cookieHandler.getCookie({ createdAt: 0, types }, consentName);

    return data ? JSON.parse(data) : { types, createdAt: 0 };
  }, [cookieHandler, cookieTypes, consentName]);

  const [consent, setConsent] = useState<Consent>(getConsent());

  useEffect(() => {
    if (consent.createdAt === 0) return;

    const data = JSON.stringify(consent);
    cookieHandler.setCookie(consent, consentName, data);
  }, [consent, consentName, cookieHandler]);

  const setCookie = useCallback(
    (name: string, data: string) =>
      cookieHandler.setCookie(consent, name, data),
    [consent, cookieHandler]
  );

  const getCookie = useCallback(
    (name: string) => cookieHandler.getCookie(consent, name),
    [consent, cookieHandler]
  );

  const value = useMemo(
    () => ({ setCookie, getCookie, setConsent, consent }),
    [setCookie, getCookie, setConsent, consent]
  );

  return (
    <CookiesContext.Provider value={value}>{children}</CookiesContext.Provider>
  );
};

function useCookies() {
  const context = useContext(CookiesContext);
  if (!context) throw new Error('useCookies requires a context!');

  return context;
}

export { CookiesProvider, useCookies };
